<template>
  <modal :show="show" @close="close" class="gray-bg" :title="title || 'Add product'">
    <template v-slot:subheader>
      <form-input
        type="text"
        :outline="true"
        :placeholder="$t('settings.liveshopping.search_placeholder')"
        :value="searchKey"
        v-model="searchKey"
      />
    </template>
    <div class="image-problem" v-if="reportImageError">
      We are having trouble loading the product images. Make sure image size and image host is configured correctly.
    </div>
    <div :class="`search-products ${noAdd ? 'no-add' : ''}`">
      <template v-if="searchState === ''">
        <div class="state">
          <i class="mdi mdi-magnify" />
          <p class="state-text">{{ $t('settings.liveshopping.search_for_products') }}</p>
        </div>
      </template>
      <template v-else-if="searchState === 'searching'">
        <div class="state">
          <i class="mdi mdi-loading mdi-spin" />
          <p class="state-text">{{ $t('settings.liveshopping.searching') }}..</p>
        </div>
      </template>
      <template v-else-if="searchState === 'searched' && searchResult.length === 0">
        <div class="state">
          <i class="mdi mdi-magnify" />
          <p class="state-text">{{ $t('settings.liveshopping.no_hits') }}</p>
        </div>
      </template>
      <template v-else>
        <div class="search-results">
          <product
            v-for="product in searchResult"
            :key="product.id"
            :index="currentAddedProducts.indexOf(`${product.id}`)"
            :product="product"
            :addProduct="true"
            :noAdd="noAdd"
            :originalImage="true"
            :state="broadcastProducts.includes(product.id) ? 'added' : addingProduct[product.id]"
            @add="addProduct(product)"
            @imageProblem="imageProblem"
          />
        </div>
      </template>
    </div>
  </modal>
</template>

<script>

import Vue from 'vue';
import Product from './ProductItem.vue';

export default {
  props: {
    noAdd: {
      type: Boolean,
      default: false,
    },
    title: {
      type: String,
      default: '',
    },
    show: {
      type: Boolean,
      default: false,
    },
  },
  components: {
    Product,
  },
  computed: {
    /**
     * Broadcast - The current broadcast in the store
     * @return {?Object}
     */
    broadcast() {
      return this.$store.getters['Broadcast/current'];
    },
    /**
     * Returns the current state of the search
     * @returns {String}
     */
    searchState() {
      return this.$store.getters['Products/searchState'];
    },
    /**
     * Returns the result of the search
     * @returns {Array}
     */
    searchResult() {
      return this.$store.getters['Products/searchResult'];
    },
    /**
     * Returns a boolean if the account as a external products provider or not
     * @returns {Boolean}
     */
    hasExternalProducts() {
      const { shoppingSettings: { provider } = {} } = this;
      return !!provider;
    },
    /**
     * Return a list of products connected to the broadcast
     * @returns {Array}
     */
    currentAddedProducts() {
      const { hasExternalProducts } = this;

      if (hasExternalProducts) {
        return this.$store.getters['Products/broadcastProducts'].map(product => product.product_id);
      }

      const { broadcast: { event_details: { shopping: { products } } } } = this;
      return products;
    },
    /**
     * Return an array of product ids from products on broadcast
     * @returns {Array}
     */
    broadcastProducts() {
      return this.$store.getters['Products/broadcastProducts'].map(product => product.handle || product.product_id);
    },
  },
  methods: {
    imageProblem() {
      this.reportImageError = true;
    },
    /**
     * Clear the search
     */
    clearSearch() {
      this.$store.dispatch('Products/clearSearchProducts');
    },
    /**
     * Search products
     */
    search() {
      const { searchKey } = this;
      this.$store.dispatch('Products/searchProducts', searchKey.toLowerCase());
    },
    /**
     * Add product to LS-list
     * @param {Object} product
     */
    async addProduct(product) {
      const {
        hasExternalProducts,
        broadcast: { id },
        currentAddedProducts: products,
      } = this;

      Vue.set(this.addingProduct, `${product.id}`, 'adding');

      if (hasExternalProducts) {
        await this.$store.dispatch('Products/addProductsToBroadcast', {
          id: this.broadcast.id,
          arr: [product.addHandle],
        });
        this.$store.dispatch('Broadcast/update', { id: this.broadcast.id, data: {} });
        await this.$store.dispatch('Products/listBroadcastProducts', id);
      } else {
        products.push(product.id);
        await this.$store.dispatch('Broadcast/update', {
          id,
          data: {
            event_details: {
              shopping: {
                products,
              },
            },
          },
        });
        await this.$store.dispatch('Broadcast/getBroadcast', { id });
      }

      Vue.delete(this.addingProduct, `${product.id}`);
    },
    close() {
      this.addingProduct = {};
      this.addState = '';
      this.searchKey = '';
      this.reportImageError = false;
      clearTimeout(this.searchTimer);
      this.$emit('close', true);
    },
  },
  watch: {
    searchKey(string) {
      clearTimeout(this.searchTimer);

      this.searchTimer = setTimeout(() => {
        if (string.trim() === '') {
          this.clearSearch();
        } else {
          this.search();
        }
      }, 500);
    },
    show(bool) {
      if (bool) {
        this.$nextTick(() => {
          try {
            this.$el.querySelector('.subheader input').focus();
          } catch (e) { /* Do nothing */ }
        });
      }
    },
  },
  data() {
    return {
      addingProduct: {},
      addState: '',
      searchKey: '',
      searchTimer: null,
      reportImageError: false,
    };
  },
};
</script>

<style lang="scss">
.search-products {
  &.no-add {
    .product-item {
      .add {
        display: none;
      }
    }
  }
}
</style>

<style lang="scss" scoped>
.image-problem {
  position: sticky;
  top: 0;
  background-color: white;
  padding: 12px;
  font-size: 16px;
  font-weight: 600;
  border-bottom: 1px solid #d9d9d9;
  text-align: center;
  color: #8d0c0c;
  z-index: 10;
}
.search-products {
  display: flex;
  flex-direction: column;
  height: 100%;
}

.search-results {
  display: flex;
  overflow: auto;
  flex: 1;
  flex-direction: column;
  padding: 16px;
  gap: 8px;
}
</style>
